import React, { useContext, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { NavigationHeader } from '@/components/NavigationHeader/NavigationHeader'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { fetchTickmillCompaniesAction } from '@/store/identity/identity.actions'
import { IdentityApiClient } from '@/utils/api/identityApi'
import { AuthSessionContext } from '@/utils/AuthContext'
import { LandingPageDto } from 'api/ClientArea/LandingPageDto'
import { UpdateLandingPageDto } from 'api/ClientArea/UpdateLandingPageDto'
import { Loading } from 'components/Loading'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchPromoMaterialsOptionsAction } from 'store/clientarea/clientarea.actions'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'

import ClientAreaAuditLogModal from '../../../components/ClientAreaAuditLogModal/ClientAreaAuditLogModal'
import { IbLandingDetailsForm } from './IbLandingDetailsForm'
import { RelatedPromoMaterialTable } from './RelatedPromoMaterialTable'

const useMapStateToProps = () => ({
  languages: useAppSelector((state) => state.clients.language),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  promoMaterialsOptions: useAppSelector((state) => state.clientArea.promoMaterialsOptions),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  return {
    fetchClientLanguages: async () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
    fetchPromoMaterialsOptions: async () =>
      dispatch(fetchPromoMaterialsOptionsAction(clientAreaApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
  }
}

const useIbLandingFetch = () => {
  const { languages, tickmillCompanies, promoMaterialsOptions } = useMapStateToProps()
  const { fetchClientLanguages, fetchPromoMaterialsOptions, fetchTickmillCompanies } =
    useMapDispatchToProps()

  const { data, ibLandingData, forceRefresh, isLoading } = usePromoMaterialsLandingsFetch()

  useEffect(() => {
    if (isEmpty(languages)) {
      fetchClientLanguages()
    }
    if (isEmpty(promoMaterialsOptions.baseUrls)) {
      fetchPromoMaterialsOptions()
    }
    if (isEmpty(tickmillCompanies)) {
      fetchTickmillCompanies()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    data,
    ibLandingData,
    forceRefresh,
    isLoading,
    promoMaterialsOptions,
    languages,
    tickmillCompanies,
  }
}

const usePromoMaterialsLandingsFetch = () => {
  const { landingId } = useLandingRoute()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [ibLandingData, setIbLanding] = useState<LandingPageDto>()

  const { callback, forceRefresh } = useCallbackWithForceRefresh(async () => {
    if (!landingId) return
    const result = await clientAreaApiClient.getLandingPageById(landingId)
    if (result) {
      setIbLanding(result)
    }
    return result
  }, [landingId]) // eslint-disable-line react-hooks/exhaustive-deps
  const { data, isLoading } = useFetchOne(callback)

  return { data, ibLandingData, isLoading, forceRefresh }
}

const useIbLandingMutate = () => {
  const { landingId } = useLandingRoute()

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const handleEditSubmit = async (data: UpdateLandingPageDto) => {
    if (landingId) {
      await clientAreaApiClient.updateLandingPage(landingId, data)
      setToast(successToast(`IB Landing Page updated successfully.`))
    }
  }

  return { handleEditSubmit }
}

const useLandingRoute = () => {
  const params = useParams<{ id: string }>()
  const landingId = params.id

  return { landingId }
}

export const IbLandingDetailsPage = () => {
  const crumbs = [{ title: 'Marketing - IB Landing Pages', link: links.ibLanding.url }]

  const landingFetch = useIbLandingFetch()
  const {
    data,
    ibLandingData,
    isLoading,
    forceRefresh,
    languages,
    tickmillCompanies,
    promoMaterialsOptions,
  } = landingFetch

  const mutate = useIbLandingMutate()

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []

  const [auditLogVisible, setAuditLogVisible] = useState(false)

  const handleSubmit = async (updateLandingPage: UpdateLandingPageDto) => {
    await mutate.handleEditSubmit(updateLandingPage)
    forceRefresh()
  }

  return (
    <React.Fragment>
      {auditLogVisible && (
        <ClientAreaAuditLogModal
          isVisible={auditLogVisible}
          onClose={() => setAuditLogVisible(false)}
          ibLandingId={ibLandingData?.id}
        />
      )}
      {ibLandingData?.name && (
        <Breadcrumbs crumbs={crumbs.concat({ title: ibLandingData.name, link: '#' })} />
      )}
      {ibLandingData && <NavigationHeader />}
      <Loading isLoading={isLoading}>
        {data && (
          <IbLandingDetailsForm
            data={data}
            languages={languages.filter((lang) => lang.clientAreaVisible)}
            tickmillCompanies={tickmillCompanies}
            promoMaterialsOptions={promoMaterialsOptions}
            setAuditLogModal={setAuditLogVisible}
            onSubmit={handleSubmit}
            permissions={permissions}
          />
        )}
      </Loading>
      {data && (
        <RelatedPromoMaterialTable
          data={data}
          promoMaterialsOptions={promoMaterialsOptions}
          permissions={permissions}
        />
      )}
    </React.Fragment>
  )
}
